import type {
  MembershipOverviewView,
  PersonalMembershipPlan,
  QuotaLimits,
  QuotaUsage,
} from './commerce';
import type { AccountThemeState, ResolvedThemeRuntime } from './theme';

/** 账户级主题会应用到所有工作台、主应用页面和组件运行时。 */
export interface AccountAppearance {
  schemaVersion: 2;
  mode: 'light' | 'dark';
  primaryColor: string;
  /** @deprecated 仅保留用于兼容已有账户；运行时背景由 mode 的中性调色板决定。 */
  canvasColor: string;
  /** @deprecated 仅保留用于兼容已有账户；运行时背景由 mode 的中性调色板决定。 */
  surfaceColor: string;
  baseRadius: number;
  density: 'compact' | 'comfortable';
  elevation: 'none' | 'soft';
  fontFamily: 'system' | 'serif' | 'mono';
  fontSize: 'small' | 'medium' | 'large';
  lineHeight: 'compact' | 'comfortable' | 'relaxed';
  contrast: 'normal' | 'high';
}

export const DEFAULT_ACCOUNT_APPEARANCE: AccountAppearance = {
  schemaVersion: 2,
  mode: 'light',
  primaryColor: '#6857ED',
  canvasColor: '#F6F7FB',
  surfaceColor: '#FFFFFF',
  baseRadius: 12,
  density: 'comfortable',
  elevation: 'soft',
  fontFamily: 'system',
  fontSize: 'medium',
  lineHeight: 'comfortable',
  contrast: 'normal',
};

/** 账户级入门进度；缺省表示已有账户，不自动开启引导。 */
export interface WorkspaceOnboarding {
  status: 'pending' | 'active' | 'completed' | 'skipped';
  workspaceId?: string;
  pageId?: string;
  packageId?: string;
  startedAt?: string;
  step?: number;
}

export interface UserView {
  id: string;
  email: string | null;
  emailVerifiedAt: string | null;
  displayName: string;
  avatar: string | null;
  locale: string;
  timezone: string;
  /** 仅控制开发入口的可见性，不代表额外权限或审核身份。 */
  developerModeEnabled: boolean;
  /** 账户是否已主动开启创意中心。 */
  creativeCenterEnabled: boolean;
  /** 账户级页面侧栏偏好，跨工作台与页面保持一致。 */
  pageSidebarPinned: boolean;
  /** 账户级外观，跨工作台、主应用与组件运行时保持一致。 */
  appearance: AccountAppearance;
  /** 新主题机制的唯一配置真源；appearance 仅为旧端与 Ant Design 的兼容投影。 */
  theme: AccountThemeState;
  /** 已鉴权解析后的主题 token 与素材引用，组件运行时不得自行请求或拼接样式。 */
  resolvedTheme: ResolvedThemeRuntime;
  /** 登录后默认回到的主界面。 */
  workspaceHomeMode: 'chat' | 'workbench';
  workspaceOnboarding?: WorkspaceOnboarding | null;
  /** 固定侧栏是否以窄轨形式显示。 */
  workspaceSidebarCollapsed: boolean;
  /** 上传文件后是否自动提炼可检索知识；由账户统一控制。 */
  knowledgeAutoOrganizeFiles: boolean;
  /** 是否允许普通 Agent 将账户知识摘要自动注入本轮上下文。 */
  knowledgeAutoRecall: boolean;
  /** 最近一次有权限访问的工作台，用于跨设备恢复入口。 */
  lastWorkspaceId: string | null;
  status: 'active' | 'disabled' | 'pendingDeletion';
  /** 仅表示个人套餐；Team 是工作台维度的会员。 */
  plan: PersonalMembershipPlan;
  quota: QuotaLimits;
  usage: QuotaUsage;
  membership?: MembershipOverviewView;
  createdAt: string;
  updatedAt: string;
}

export interface AuthResultView {
  user: UserView;
  accessToken: string;
}

/** 原生端自行保管轮换令牌；浏览器端永远不返回此字段。 */
export interface MobileAuthResultView extends AuthResultView {
  refreshToken: string;
}

export type AuthLoginMethod = 'email_password' | 'phone_sms' | 'wechat_web' | 'wechat_mini_program';

export interface AuthRegistrationPolicy {
  registrationEnabled: boolean;
  invitationRequired: boolean;
}

export interface AuthLoginMethodsView extends AuthRegistrationPolicy {
  emailPassword: boolean;
  emailVerificationRequiredOnRegister: boolean;
  phoneSms: boolean;
  wechatWeb: boolean;
  wechatMiniProgram: boolean;
}

export interface AuthIdentityView {
  id: string;
  method: AuthLoginMethod;
  label: string;
  verifiedAt: string;
  lastUsedAt: string | null;
}

export interface PhoneChallengeView {
  challengeId: string;
  expiresAt: string;
  resendAt: string;
}

export type ExternalAuthFlowView =
  | AuthResultView
  | {
      status: 'unbound';
      pendingToken: string;
      expiresAt: string;
    };

export interface WechatWebTransactionView {
  id: string;
  pollToken: string;
  authorizationUrl: string;
  expiresAt: string;
}

/** 小程序 code 登录：已绑定直接返回会话，否则只能用短时 bindToken 完成一次绑定或注册。 */
export type WechatMiniProgramLoginView =
  | ({ status: 'bound' } & MobileAuthResultView)
  | { status: 'unbound'; bindToken: string; expiresAt: string };
